<div class="container-fluid d-flex align-items-center justify-content-center op-home-container">
  <div class="home-card card shadow d-flex flex-column">
    <!-- Card Header with Actions -->
    <div class="card-header d-flex justify-content-between align-items-center">
      <nav aria-label="breadcrumb" class="flex-grow-1">
        <ol class="breadcrumb mb-0 align-items-center bg-light px-2 py-1 rounded-pill">
          <li class="breadcrumb-item">
            <a href="javascript:void(0)" (click)="navigateToBreadcrumb(-1)" class="text-decoration-none">
              <i class="bi bi-house-door-fill"></i> Root
            </a>
          </li>
          @for (folder of currentPath; track folder.guid; let i = $index) {
            <li class="breadcrumb-item">
              @if (i < currentPath.length - 1) {
                <a href="javascript:void(0)" (click)="navigateToBreadcrumb(i)" class="text-decoration-none">{{ folder.name }}</a>
              } @else {
                <span>{{ folder.name }}</span>
              }
            </li>
          }
        </ol>
      </nav>
      <div class="d-flex gap-2 ms-3">
        <!-- Global Search -->
        <div class="input-group input-group-sm global-search-input">
          <span class="input-group-text bg-white border-end-0"><i class="bi bi-search"></i></span>
          <input
            type="text"
            class="form-control border-start-0"
            placeholder="Search all folders..."
            [ngModel]="globalSearchTerm"
            (ngModelChange)="onGlobalSearchChange($event)"
          />
          @if (globalSearchTerm) {
            <button class="btn btn-outline-secondary btn-sm" (click)="clearGlobalSearch()" type="button">
              <i class="bi bi-x-lg"></i>
            </button>
          }
        </div>
        <button class="btn btn-sm btn-outline-secondary" (click)="refreshCurrentView()" [disabled]="isLoading" title="Refresh View">
          <i class="bi bi-arrow-clockwise" [class.spinner-border]="isLoading" [style.width.rem]="isLoading ? 1 : ''" [style.height.rem]="isLoading ? 1 : ''"></i>
          <span class="d-none d-md-inline"> Refresh</span>
        </button>
        <button class="btn btn-sm btn-success" (click)="navigateToCreateFolder()" [disabled]="!canCreateFolder() || isLoading" *hasCapability="CapabilityType.CREATE_FOLDER">
          <i class="bi bi-folder-plus me-2"></i><span class="d-none d-md-inline"> New Folder</span>
        </button>
        <button class="btn btn-sm btn-primary" (click)="navigateToCreatePlanogram()" [disabled]="isLoading" *hasCapability="CapabilityType.CREATE_PLANOGRAM">
          <i class="bi bi-file-earmark-plus me-2"></i><span class="d-none d-md-inline"> New Planogram</span>
        </button>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body position-relative d-flex flex-grow-1 p-0">

      <!-- Global Search Results Overlay (always rendered, visibility controlled via CSS) -->
      <div class="global-search-overlay" [class.active]="isGlobalSearchMode">
        @if (isGlobalSearching) {
          <div class="loading-container d-flex flex-column align-items-center justify-content-center h-100">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">Searching...</p>
          </div>
        } 
        @else if (isGlobalSearchMode && globalSearchResults.length === 0) {
          <div class="text-center text-muted mt-5">
            <i class="bi bi-search fs-1"></i>
            <p class="mt-2">No planograms found matching "{{ globalSearchTerm }}"</p>
          </div>
        } 
        @else if (isGlobalSearchMode) {
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">
              <i class="bi bi-globe me-2"></i>Search Results
              <span class="badge bg-secondary ms-2">{{ globalSearchResults.length }}</span>
            </h5>
          </div>
          <div class="planogram-list list-group list-group-flush">
            @for (vm of searchResultsVm; track vm.result.planogram.guid) {
              <div class="list-group-item d-flex justify-content-between align-items-start py-3">
                <div class="d-flex gap-2 flex-grow-1">
                  <div>
                    <div class="fw-medium">
                      {{ vm.latestStateName }}
                      @if (vm.badges.approved && !vm.badges.inProgress) {
                        <span class="badge bg-success ms-1">Approved</span>
                      }
                      @if (vm.badges.inProgress && !vm.badges.approved) {
                        <span class="badge bg-warning text-dark ms-1">In Progress</span>
                      }
                      @if (vm.badges.approved && vm.badges.inProgress) {
                        <span class="badge bg-success ms-1">Approved</span>
                        <span class="badge bg-warning text-dark ms-1">In Progress</span>
                      }
                      @if (vm.result.planogram.shared) {
                        <span class="badge bg-primary ms-1">Shared</span>
                      }
                    </div>
                    <small class="text-muted">Store: {{ vm.result.planogram.storeName }} | Merch: {{ vm.result.planogram.merchName }}</small><br/>
                    <small class="text-muted">
                      <i class="bi bi-folder2 me-1"></i>{{ vm.result.folderPath }}
                    </small><br/>
                    <small class="text-muted">Created: {{ vm.result.planogram.dateCreated | date:"MMM d, y, h:mm a" }}</small><br/>
                    <small class="text-muted">Last Edited: {{ vm.result.planogram.dateLastEdited | date:"MMM d, y, h:mm a" }}</small><br/>
                    <small class="text-muted" *ngIf="vm.badges.publishedDate">Date Published: {{ vm.badges.publishedDate | date:"MMM d, y, h:mm a" }}</small>
                  </div>
                </div>
                <div class="d-flex gap-2 flex-column h-100 justify-content-around">
                  <button class="btn btn-outline-secondary btn-sm" (click)="navigateToSearchResultFolder(vm.result)" title="Go to folder">
                    <i class="bi bi-folder-symlink"></i>
                  </button>
                  <button class="btn btn-outline-primary" (click)="openSearchResultPlanogram(vm.result)">Open</button>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Folders Sidebar -->
      <div class="folders-sidebar border-end p-3">
        @if (isLoading && displayedFolders.length === 0) {
          <div class="loading-container d-flex flex-column align-items-center justify-content-center h-100">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">Loading...</p>
          </div>
        } @else {
          <div class="folder-list list-group list-group-flush">
            @for (folder of displayedFolders; track folder.guid) {
              <a href="javascript:void(0)" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center" (click)="selectFolder(folder)">
                <div>
                  <i class="bi bi-folder-fill me-2 text-warning"></i>
                  {{ folder.name }}
                  @if (folder.defaultFolder) {
                    <span class="badge bg-info ms-2">Default</span>
                  }
                </div>
                <div class="d-flex align-items-center gap-2">
                  @if (folder.parentFolderId) {
                    <button class="btn btn-sm btn-outline-danger" (click)="deleteFolder($event, folder)"><i class="bi bi-trash"></i></button>
                  }
                  <i class="bi bi-chevron-right"></i>
                </div>
              </a>
            }
          </div>
        }
      </div>

      <!-- Planograms Content -->
      <div class="planograms-content flex-grow-1 p-3">
        @if (isLoading) {
          <div class="loading-container d-flex flex-column align-items-center justify-content-center h-100">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">Loading...</p>
          </div>
        }
        @if (!isLoading && displayedPlanograms.length > 0) {
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">Planograms</h5>
            <div class="d-flex gap-2 w-50">
              <button class="btn btn-sm btn-warning text-nowrap" (click)="openMovePlanogramsModal()" [disabled]="!hasSelectedPlanograms" *hasCapability="CapabilityType.MOVE_PLANOGRAM">
                <i class="bi bi-folder-symlink me-1"></i> Move Selected
              </button>
              <button class="btn btn-sm btn-outline-warning text-nowrap" (click)="openBulkClonePage()" [disabled]="!hasSelectedPlanograms" *hasCapability="CapabilityType.CLONE_PLANOGRAM">
                <i class="bi bi-files me-1"></i> Bulk Clone
              </button>
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-white border-end-0"><i class="bi bi-search"></i></span>
                <input type="text" class="form-control border-start-0" placeholder="Search name, store or merch..." [ngModel]="searchTerm" (ngModelChange)="onSearchTermChange($event)"/>
              </div>
            </div>
          </div>

          <cdk-virtual-scroll-viewport [itemSize]="planogramRowHeight" class="planogram-list">
            <div *cdkVirtualFor="let vm of displayedPlanogramsVm; trackBy: trackByPlanogramGuid"
                 class="list-group-item planogram-row d-flex justify-content-between align-items-center py-3 gap-3">
              <div class="d-flex gap-2 flex-grow-1 align-items-center overflow-hidden">
                <input type="checkbox" class="form-check-input flex-shrink-0" [checked]="vm.selected" (change)="togglePlanogramSelection(vm, $event)" *hasCapability="CapabilityType.MOVE_PLANOGRAM"/>
                <div class="flex-grow-1 overflow-hidden">
                  <div class="fw-medium text-truncate">
                    {{ vm.latestStateName }}
                    @if (vm.badges.approved && !vm.badges.inProgress) {
                      <span class="badge bg-success ms-1">Approved</span>
                    }
                    @if (vm.badges.inProgress && !vm.badges.approved) {
                      <span class="badge bg-warning text-dark ms-1">In Progress</span>
                    }
                    @if (vm.badges.approved && vm.badges.inProgress) {
                      <span class="badge bg-success ms-1">Approved</span>
                      <span class="badge bg-warning text-dark ms-1">In Progress</span>
                    }
                    @if (vm.planogram.shared) {
                      <span class="badge bg-primary ms-1">Shared</span>
                    }
                    @if (vm.isLinked) {
                      <span class="badge bg-info text-dark ms-1"><i class="bi bi-link-45deg"></i> Linked</span>
                    }
                  </div>
                  <div class="small text-muted text-truncate mt-1">
                    <span class="me-3"><i class="bi bi-shop me-1"></i>{{ vm.planogram.storeName }}</span>
                    <span><i class="bi bi-grid-3x3-gap me-1"></i>{{ vm.planogram.merchName }}</span>
                  </div>
                  <div class="small text-muted d-flex flex-wrap gap-3 mt-1">
                    <span><i class="bi bi-calendar-plus me-1"></i>Created {{ vm.planogram.dateCreated | date:"MMM d, y, h:mm a" }}</span>
                    <span><i class="bi bi-pencil-square me-1"></i>Edited {{ vm.planogram.dateLastEdited | date:"MMM d, y, h:mm a" }}</span>
                    <span *ngIf="vm.badges.publishedDate"><i class="bi bi-check2-circle me-1"></i>Published {{ vm.badges.publishedDate | date:"MMM d, y, h:mm a" }}</span>
                  </div>
                </div>
              </div>
              <div class="d-flex gap-2 align-items-center flex-shrink-0">
                <button class="btn btn-outline-primary" (click)="openPlanogram(vm.planogram)">
                  <i class="bi bi-box-arrow-up-right me-1"></i>Open
                </button>
                <ng-container *hasCapability="CapabilityType.CLONE_PLANOGRAM">
                  @if (vm.isLinked) {
                    <button class="btn btn-outline-warning" (click)="unlinkPlanogram(vm.planogram)" title="Convert to a standalone, editable copy">
                      <i class="bi bi-scissors me-1"></i>Unlink
                    </button>
                  } @else {
                    <button class="btn btn-outline-warning" *ngIf="vm.hasStates" (click)="clonePlanogram(vm.planogram)">
                      <i class="bi bi-files me-1"></i>Clone
                    </button>
                    <button class="btn btn-outline-info" *ngIf="vm.hasStates" (click)="createReference(vm.planogram)" title="Create linked planograms for other stores">
                      <i class="bi bi-link-45deg me-1"></i>Reference
                    </button>
                  }
                </ng-container>
                <button class="btn btn-outline-danger ms-1" (click)="deletePlanogram($event, vm.planogram)" title="Delete Planogram" *hasCapability="CapabilityType.DELETE_PLANOGRAM">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        }
        @if (!isLoading && displayedPlanograms.length === 0 && currentPath.length > 0) {
          <div class="text-center text-muted mt-5">
            <i class="bi bi-folder-x fs-1"></i>
            <p class="mt-2">This folder is empty.</p>
          </div>
        }
        @if (!isLoading && (currentPath.length === 0 || (currentPath.length === 0 && displayedFolders.length === 0))) {
            @if (displayedFolders.length === 0) {
              <div class="text-center text-muted mt-5">
                <i class="bi bi-house-x fs-1"></i>
                <p class="mt-2">No root folders available.</p>
              </div>
            }
        }
      </div>
    </div>
  </div>
</div>